import { useState, useEffect } from 'react'
import { 
  Users, Crown, Zap, Heart, Shield, Trophy, 
  ArrowLeft, Target, Sparkles, UserPlus, Swords
} from 'lucide-react'
import { getTeams, createTeam, joinTeam, updateTeamScore, subscribeToRoom } from '../utils/multiplayerAPI.js'
import './PartyMode.css'

// Game stages for Easter story
const GAME_STAGES = [
  {
    id: 'love',
    name: 'Guardian of Love',
    icon: Heart,
    color: '#ff6b9d',
    prompt: 'What is the first big truth of Easter?',
    clue: 'It tells us why the story is good news for everybody.',
    options: [
      { label: 'God loves the whole world', correct: true, power: 'HEAL' },
      { label: 'Nobody mattered', correct: false },
      { label: 'The story has no meaning', correct: false },
    ]
  },
  {
    id: 'cross',
    name: 'Sentinel of Sacrifice',
    icon: Swords,
    color: '#ff4757',
    prompt: 'Why did Jesus go to the cross?',
    clue: 'Think about sacrifice, rescue, and love.',
    options: [
      { label: 'To show love and save us from sin', correct: true, power: 'SHIELD' },
      { label: 'To win a game', correct: false },
      { label: 'To hide from everyone', correct: false },
    ]
  },
  {
    id: 'tomb',
    name: 'Keeper of the Seal',
    icon: Shield,
    color: '#74b9ff',
    prompt: 'What did the women discover on the third day?',
    clue: 'This surprise changed history forever.',
    options: [
      { label: 'The tomb was empty', correct: true, power: 'BURST' },
      { label: 'The stone was made of gold', correct: false },
      { label: 'The garden disappeared', correct: false },
    ]
  },
  {
    id: 'rise',
    name: 'Champion of Life',
    icon: Crown,
    color: '#ffd700',
    prompt: 'What is the Easter message for us today?',
    clue: 'It is the hope we celebrate together.',
    options: [
      { label: 'Jesus is risen and gives new life', correct: true, power: 'VICTORY' },
      { label: 'The story ended in sadness', correct: false },
      { label: 'Hope only belongs to the past', correct: false },
    ]
  }
]

const POWER_UPS = {
  HEAL: { icon: Heart, color: '#ff6b9d', name: 'Love Shield' },
  SHIELD: { icon: Shield, color: '#74b9ff', name: 'Faith Guard' },
  BURST: { icon: Zap, color: '#ffd700', name: 'Hope Burst' },
  VICTORY: { icon: Trophy, color: '#00d26a', name: 'Eternal Victory' }
}

export default function PartyMode({ 
  roomId, 
  playerId, 
  players, 
  gameState, 
  currentStage, 
  score,
  onLeave 
}) {
  const [teams, setTeams] = useState([])
  const [myTeam, setMyTeam] = useState(null)
  const [showTeamSelector, setShowTeamSelector] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [feedback, setFeedback] = useState('')
  const [feedbackType, setFeedbackType] = useState('idle')
  const [powers, setPowers] = useState([])
  const [teamScore, setTeamScore] = useState(0)
  
  const currentPlayer = players.find(p => p.id === playerId)
  const currentStageData = GAME_STAGES[currentStage || 0]
  const StageIcon = currentStageData?.icon || Heart
  
  useEffect(() => {
    // Fetch teams
    const fetchTeams = async () => {
      try {
        const teamData = await getTeams(roomId)
        setTeams(teamData)
        
        // Find my team
        if (currentPlayer?.team_id) {
          const team = teamData.find(t => t.id === currentPlayer.team_id)
          setMyTeam(team)
          setTeamScore(team?.score || 0)
        }
      } catch (err) {
        console.error('Error fetching teams:', err)
      }
    }
    fetchTeams()
    
    // Subscribe to updates
    const subscription = subscribeToRoom(roomId, () => {
      fetchTeams()
    })
    
    return () => subscription.unsubscribe()
  }, [roomId, currentPlayer?.team_id])
  
  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return
    
    try {
      const team = await createTeam(roomId, newTeamName)
      await joinTeam(roomId, playerId, team.id)
      setMyTeam(team)
      setShowTeamSelector(false)
      setNewTeamName('')
    } catch (err) {
      console.error('Error creating team:', err)
    }
  }
  
  const handleJoinTeam = async (teamId) => {
    try {
      await joinTeam(roomId, playerId, teamId)
      const team = teams.find(t => t.id === teamId)
      setMyTeam(team)
      setShowTeamSelector(false)
    } catch (err) {
      console.error('Error joining team:', err)
    }
  }
  
  const handleAnswer = async (option) => {
    if (!option.correct) {
      setFeedback('Not quite right! Your team supports you!')
      setFeedbackType('error')
      return
    }
    
    const points = 100
    const newPowers = option.power ? [...powers, option.power] : powers
    const newScore = teamScore + points
    
    setPowers(newPowers)
    setTeamScore(newScore)
    setFeedback(`+${points} points for ${myTeam?.name}! 🎉`)
    setFeedbackType('success')
    
    // Update team score
    if (myTeam) {
      await updateTeamScore(roomId, myTeam.id, newScore)
    }
  }
  
  // Calculate team rankings
  const sortedTeams = [...teams].sort((a, b) => (b.score || 0) - (a.score || 0))
  const myTeamRank = sortedTeams.findIndex(t => t.id === myTeam?.id) + 1
  
  if (!myTeam) {
    // Team selection view
    return (
      <main className="party-mode">
        <div className="party-background">
          <div className="floating-icons">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="floating-team-icon" style={{ 
                animationDelay: `${i * 0.7}s`,
                left: `${Math.random() * 100}%`
              }}>
                <Users size={24} />
              </div>
            ))}
          </div>
        </div>
        
        <div className="team-selection">
          <button className="back-btn" onClick={onLeave}>
            <ArrowLeft size={20} />
            Back
          </button>
          
          <h1 className="party-title">
            <Sparkles size={32} />
            Party Mode
          </h1>
          
          <p className="party-subtitle">Join or create a team to play together!</p>
          
          <div className="teams-grid">
            {teams.map((team, index) => (
              <div key={team.id} className="team-card">
                <div className="team-rank">#{index + 1}</div>
                <h3 className="team-name">{team.name}</h3>
                <div className="team-stats">
                  <Users size={16} />
                  <span>{team.players?.length || 0} members</span>
                </div>
                <div className="team-score">
                  <Trophy size={16} />
                  <span>{(team.score || 0).toLocaleString()} pts</span>
                </div>
                <button 
                  className="join-team-btn"
                  onClick={() => handleJoinTeam(team.id)}
                >
                  <UserPlus size={18} />
                  Join Team
                </button>
              </div>
            ))}
            
            <div className="create-team-card">
              <h3>Create New Team</h3>
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Team name..."
                maxLength={20}
              />
              <button 
                className="create-team-btn"
                onClick={handleCreateTeam}
                disabled={!newTeamName.trim()}
              >
                <Users size={18} />
                Create Team
              </button>
            </div>
          </div>
        </div>
      </main>
    )
  }
  
  // Active gameplay view
  return (
    <main className="party-mode active">
      {/* Background */}
      <div className="party-background">
        <div className="team-glow" style={{ '--team-color': myTeamRank === 1 ? '#ffd700' : '#74b9ff' }} />
      </div>
      
      {/* Header */}
      <header className="party-header">
        <div className="header-left">
          <button className="back-btn" onClick={onLeave}>
            <ArrowLeft size={18} />
          </button>
          <div className="team-badge">
            <Users size={20} />
            <span>{myTeam?.name}</span>
          </div>
        </div>
        
        <div className="header-center">
          <div className="team-rank-badge">
            <Crown size={18} />
            <span>Rank #{myTeamRank}</span>
          </div>
        </div>
        
        <div className="header-right">
          <div className="team-total-score">
            <Trophy size={20} />
            <span>{teamScore.toLocaleString()}</span>
          </div>
        </div>
      </header>
      
      {/* Team Members */}
      <section className="team-members-bar">
        {myTeam?.players?.map((player, index) => (
          <div key={player.id} className={`team-member ${player.id === playerId ? 'you' : ''}`}>
            <div className="member-avatar">{player.name[0]}</div>
            <span className="member-name">{player.name}</span>
            {player.id === playerId && <span className="you-tag">YOU</span>}
          </div>
        ))}
      </section>
      
      {/* Game Arena */}
      <section className="party-arena">
        {/* Boss */}
        <div className="party-boss">
          <div className="boss-avatar" style={{ '--boss-color': currentStageData?.color }}>
            <StageIcon size={60} />
          </div>
          <h2 className="boss-name">{currentStageData?.name}</h2>
        </div>
        
        {/* Question */}
        <div className="party-question">
          <h3>{currentStageData?.prompt}</h3>
          <p className="clue">{currentStageData?.clue}</p>
        </div>
        
        {/* Answer Options */}
        <div className="party-answers">
          {currentStageData?.options.map((option, index) => (
            <button
              key={index}
              className="party-answer-btn"
              onClick={() => handleAnswer(option)}
            >
              <span className="answer-key">{['A', 'B', 'C'][index]}</span>
              <span className="answer-text">{option.label}</span>
              {option.power && powers.includes(option.power) && (
                <span className="collected-power">
                  {(() => {
                    const Icon = POWER_UPS[option.power]?.icon
                    return Icon ? <Icon size={14} /> : null
                  })()}
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Feedback */}
        {feedback && (
          <div className={`party-feedback ${feedbackType}`}>
            {feedbackType === 'success' ? <Sparkles size={20} /> : <Target size={20} />}
            <span>{feedback}</span>
          </div>
        )}
      </section>
      
      {/* Other Teams Progress */}
      <section className="other-teams">
        <h4>Other Teams</h4>
        <div className="teams-progress">
          {sortedTeams.filter(t => t.id !== myTeam?.id).map((team, index) => (
            <div key={team.id} className="other-team">
              <span className="other-team-name">{team.name}</span>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${Math.min(((team.score || 0) / 1000) * 100, 100)}%` }}
                />
              </div>
              <span className="other-team-score">{(team.score || 0).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </section>
      
      {/* Collected Powers */}
      {powers.length > 0 && (
        <footer className="powers-footer">
          <span className="powers-label">Team Powers:</span>
          <div className="powers-row">
            {powers.map((power, i) => (
              <div 
                key={i} 
                className="power-badge"
                style={{ color: POWER_UPS[power]?.color }}
              >
                {(() => {
                  const Icon = POWER_UPS[power]?.icon
                  return Icon ? <Icon size={20} /> : null
                })()}
                <span>{POWER_UPS[power]?.name}</span>
              </div>
            ))}
          </div>
        </footer>
      )}
    </main>
  )
}
